/**
 * API Client for Backend Communication
 * Replaces direct external API calls with calls to our backend
 *
 * CRITICAL: All date transformations now use safe date validation
 */

import { createDateWithFallback, getCurrentDate } from '../utils/dateValidation';
import { getPass } from './pressPass';

interface APIResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  errors?: string[];
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  meta: {
    timestamp: Date;
    version: string;
    requestId: string;
    dataSource: string;
    cached: boolean;
    cacheAge?: number;
  };
}

export interface BackendGame {
  id: string;
  sport: 'college-football' | 'nfl';
  externalId: string;
  season: number;
  week: number;
  seasonType: 'regular' | 'preseason' | 'postseason';
  scheduledAt: string;
  startedAt?: string;
  completedAt?: string;
  status: 'scheduled' | 'in-progress' | 'completed' | 'postponed' | 'cancelled';

  homeTeam: {
    id: string;
    name: string;
    displayName: string;
    abbreviation: string;
    logo: string;
    primaryColor: string;
    conference?: string;
    score?: number;
  };

  awayTeam: {
    id: string;
    name: string;
    displayName: string;
    abbreviation: string;
    logo: string;
    primaryColor: string;
    conference?: string;
    score?: number;
  };

  venue?: {
    name: string;
    city: string;
    state?: string;
  };

  isNeutralSite: boolean;
  attendance?: number;
  broadcast?: string[];
  temperature?: number;
  weatherDescription?: string;
  dataQuality: number;
  lastUpdated: string;
  createdAt: string;
  updatedAt: string;
}

// ---------------------------------------------------------------------------
// Scoreboard types — leaner contract for the ESPN proxy endpoint.
// Do NOT reuse BackendGame here: it carries Postgres-specific fields (week,
// season, seasonType, dataQuality, etc.) that are meaningless for MLB/NBA/
// NHL/Soccer.
// ---------------------------------------------------------------------------

export interface ScoreboardTeam {
  name: string;
  displayName: string;
  abbreviation: string;
  logo: string | null;
  primaryColor: string | null;
  score?: number;
  espnTeamId?: string;
}

export interface ScoreboardGame {
  id: string;
  /** Registry key, e.g. 'mlb' */
  league: string;
  status: 'scheduled' | 'in-progress' | 'completed';
  /** ISO datetime string */
  scheduledAt: string;
  homeTeam: ScoreboardTeam;
  awayTeam: ScoreboardTeam;
  /** Optional — soccer/international events often omit venue */
  venue?: { name: string; city?: string };
  broadcast?: string[];
  statusDetail?: string;
  period?: number;
  clock?: string;
  attendance?: number;
  odds?: {
    provider?: string;
    details?: string;
    spread?: number;
    overUnder?: number;
  };
}

// ---------------------------------------------------------------------------
// Prediction types — the "full matchup breakdown" served by /api/predictions.
// Mirrors backend/src/types ScoreboardPrediction + MatchupBreakdown.
// ---------------------------------------------------------------------------

export interface PredictionStat {
  name: string;
  homeValue: number | string;
  awayValue: number | string;
}

export interface PredictionRecentGame {
  opponent: string;
  result: 'W' | 'L';
  score: string;
}

export interface PredictionTeamDetails {
  record: string;
  recentGames: PredictionRecentGame[];
}

export type BetMarket = 'moneyline' | 'spread' | 'total' | 'player_prop';
export type BetSide = 'home' | 'away' | 'draw' | 'over' | 'under';
export type BetOutcome = 'win' | 'loss' | 'push' | 'ungraded';

export interface BetSelection {
  market: BetMarket;
  side: BetSide;              // player_prop: 'over' | 'under' only
  line?: number;              // required for spread/total/player_prop
  playerName?: string;        // player_prop only: full name as listed in box scores
  propType?: string;          // player_prop only: whitelisted stat key
}

export interface PredictionBet {
  type: string;
  pick: string;
  reason: string;
  selection?: BetSelection;
}

export interface PredictionParlayLeg {
  pick: string;
  reason: string;
  selection?: BetSelection;
}

export interface GroundingSource {
  uri: string;
  title: string;
}

export interface MatchupBreakdown {
  headline: string;
  winnerSide: 'home' | 'away' | 'draw';
  winnerName: string;
  predictedHomeScore: number;
  predictedAwayScore: number;
  homeWinProbability: number;
  awayWinProbability: number;
  /** 3-way leagues only (soccer); home/draw/away sum to ~100. Undefined for 2-way. */
  drawWinProbability?: number;
  confidence: number;
  /** One-sentence TL;DR ("The Read"). Optional — pre-field rows fall back to theLean's first sentence. */
  theRead?: string;
  theLean: string;
  keyFactors: string[];
  theVibe: string;
  analysis: string;
  stats: PredictionStat[];
  homeDetails: PredictionTeamDetails;
  awayDetails: PredictionTeamDetails;
  suggestedBets: PredictionBet[];
  parlayLegs?: PredictionParlayLeg[];
  /** GET CRAZY extension legs stacking on parlayLegs; player props allowed. */
  crazyLegs?: PredictionParlayLeg[];
  responsibleGamblingNote: string;
}

export interface OddsConsensus {
  moneylineHome?: number;
  moneylineAway?: number;
  moneylineDraw?: number;     // 3-way (soccer) only
  spreadHome?: number;
  total?: number;
  impliedHomeWinPct?: number;
  impliedAwayWinPct?: number;
  impliedDrawPct?: number;    // 3-way (soccer) only
  bookCount: number;
}

/** One graded Ticket leg (mirrors a suggestedBet by index). */
export interface GradedLeg {
  pick: string;
  market: BetMarket;
  outcome: BetOutcome;
}

export interface ParlayGrading {
  result: 'hit' | 'miss' | 'ungraded';
  legs: GradedLeg[];
}

/** The graded post-mortem attached once a game is final. */
export interface PredictionGrading {
  winnerCorrect: boolean;
  bets: GradedLeg[];
  parlay: ParlayGrading | null;
  /** Full-stack (safe + crazy) verdict; legs carries crazy legs only. Null/absent when no crazyLegs. */
  crazyParlay?: ParlayGrading | null;
  gradedAt: string;
}

export interface GamePrediction {
  league: string;
  espnEventId: string;
  modelName: string;
  status: 'scheduled' | 'in-progress' | 'completed';
  scheduledAt: string;
  homeTeam: ScoreboardTeam;
  awayTeam: ScoreboardTeam;
  venue?: { name: string; city?: string };
  breakdown: MatchupBreakdown;
  odds?: OddsConsensus;
  groundingSources: GroundingSource[];
  generatedAt: string;
  updatedAt: string;
  refreshCount?: number;
  changeReason?: string | null;
  isCorrect?: boolean | null;
  result?: { homeScore: number; awayScore: number; winnerSide: 'home' | 'away' | 'draw' } | null;
  grading?: PredictionGrading | null;
}

export interface PredictionRecordBucket {
  wins: number;
  losses: number;
  total: number;
  winPct: number;
  /** Optional when the backend can price historical tickets. */
  netUnits?: number;
  /** Optional return on risked units. */
  roi?: number;
  /** Optional count of graded decisions without usable pricing. */
  unpriced?: number;
}

export interface RecordBreakdownRow {
  moneyline: PredictionRecordBucket;
  spread: PredictionRecordBucket;
  totals: PredictionRecordBucket;
  parlay: PredictionRecordBucket;
  crazyParlay: PredictionRecordBucket;
}

/**
 * Flat 1u-per-pick ledger for a market. `unpriced` counts decided picks with no
 * stored price that therefore sit out of the ledger (surfaced as fine print).
 */
export interface UnitsBucket {
  bets: number;
  units: number;
  roiPct: number;
  unpriced?: number;
}

export interface RecordUnits {
  overall: UnitsBucket;
  moneyline: UnitsBucket;   // priced at the published line
  spread: UnitsBucket;      // standard -110
  totals: UnitsBucket;      // standard -110
}

/** Moneyline (the Lean) streaks, in graded order. */
export interface RecordStreaks {
  current: { kind: 'W' | 'L'; length: number } | null;
  longestWin: number;
  longestLoss: number;
  bestDay: { date: string; wins: number; losses: number } | null;
}

export interface BaselinePair {
  ai: PredictionRecordBucket;
  baseline: PredictionRecordBucket;
}

/** AI vs the market-baseline shadow model, on games BOTH models graded. */
export interface BaselineComparison {
  baselineModel: string;
  moneyline: BaselinePair;
  spread: BaselinePair;
  totals: BaselinePair;
}

/**
 * A single graded bet leg — the atomic row the pivot engine slices. One per
 * graded market per game, stamped with the game's lean context. `units` is
 * unrounded; round for display.
 */
export type LegMarket = 'moneyline' | 'spread' | 'total';
export type LegOutcome = 'win' | 'loss';
export type SideType = 'favorite' | 'underdog' | 'pickem';
export type OddsBand = 'heavy-fav' | 'fav' | 'pickem' | 'dog' | 'long-dog';
export type ConfidenceTier = 'lock' | 'lean' | 'toss-up';

export interface RecordLeg {
  id: string;
  date: string;               // NY game day (YYYY-MM-DD)
  league: string;
  matchup: string;            // "Away @ Home"
  market: LegMarket;
  outcome: LegOutcome;
  units: number;
  unpriced: boolean;
  pickedSide: 'home' | 'away' | 'draw';
  sideType: SideType | null;
  oddsBand: OddsBand | null;
  confidence: number | null;
  confidenceTier: ConfidenceTier | null;
}

/** Per-market scorecard: moneyline (the Lean), spread (ATS), totals, parlay, full stack. */
export interface PredictionRecord {
  moneyline: PredictionRecordBucket;
  spread: PredictionRecordBucket;
  totals: PredictionRecordBucket;
  parlay: PredictionRecordBucket;
  crazyParlay: PredictionRecordBucket;
  byLeague: Array<RecordBreakdownRow & { league: string }>;
  byDay: Array<RecordBreakdownRow & { date: string; units?: number }>;  // all-time, newest-first
  last7Days: PredictionRecordBucket;
  // Optional on the wire: a backend still serving the pre-ledger payload omits
  // these, and the record view degrades instead of crashing (deploy skew).
  units?: RecordUnits;
  streaks?: RecordStreaks;
  vsBaseline?: BaselineComparison | null;
}

// ---------------------------------------------------------------------------
// Props types — The Big Board. Mirrors backend propsService.ts payloads
// (design.md §8), the FROZEN P3 UI contract. Rates default to the OVER side;
// each row carries both sides so the UI can toggle.
// ---------------------------------------------------------------------------

export interface PropBestPrice {
  book: string;
  price: number;
}

/** made / total appearances / made-rate percentage over a window. */
export interface PropWindowRates {
  made: number;
  total: number;
  pct: number;
}

/**
 * Six hit-rate windows. The four gated windows (s2025/h2h/l5/l20) come back
 * `null` in a free-tier board payload (the Press Pass split, decisions.md D12);
 * season (s2026) + L10 are always present. Player-page payloads (always full)
 * populate all six. Render null as a locked "—".
 */
export interface PropRates {
  s2025: PropWindowRates | null;
  s2026: PropWindowRates;
  h2h: PropWindowRates | null;
  l5: PropWindowRates | null;
  l10: PropWindowRates;
  l20: PropWindowRates | null;
}

/**
 * The Desk Grade for a prop's OVER side (decisions.md D14): a weighted A–F letter
 * with its 0-100 score and the raw inputs behind it (for the InfoTip breakdown).
 * `null` = ungradeable OR the free tier (depth stays premium). `splitEdge` is
 * unwired in v1; `lineValue` is a probability delta or null when the prop has no odds.
 */
export interface DeskGrade {
  letter: 'A' | 'B' | 'C' | 'D' | 'F';
  score: number;
  inputs: {
    l10Pct: number | null;
    seasonPct: number | null;
    splitEdge: number | null;
    streak: number;
    lineValue: number | null;
  };
}

export interface BoardRow {
  player: { id: number; name: string; teamAbbrev: string | null; position: string | null; batSide: string | null; headshot: string | null };
  eventId: number;
  propType: string;
  line: number | null;
  over: { best: PropBestPrice | null; books: number };
  under: { best: PropBestPrice | null; books: number };
  averages: { l5: number; l10: number; season: number };
  rates: PropRates;
  streak: number;
  grade: DeskGrade | null;
  matchup: { oppStarter: { name: string; hand: string | null } | null; avgVsHand: number | null };
  lineupSlot: { order: number | null; confirmed: boolean } | null;
}

export interface BoardGame {
  eventId: number;
  matchup: string;
  start: string;
}

export interface BoardPayload {
  generatedAt: string;
  oddsAsOf: string | null;
  games: BoardGame[];
  rows: BoardRow[];
  capped: boolean;
  /** 'free' = the teaser board a no-pass visitor sees (gated windows nulled). */
  tier: 'free' | 'full';
}

export interface PropsMeta {
  league: string;
  categories: string[];
  gamesToday: number;
  books: string[];
  lanes: Array<{ job: string; lastOkAt: string | null; ageSeconds: number | null }>;
}

// ---------------------------------------------------------------------------
// Player page — mirrors backend propsService.ts `PlayerPayload` (design.md §8).
// Always full-tier (player pages are above the free/paid split line, D12).
// ---------------------------------------------------------------------------

/** One game in the log: the stat value that game, with matchup context. */
export interface PropGameStatPoint {
  eventId: number;
  date: string; // YYYY-MM-DD
  opponentAbbrev: string;
  isHome: boolean;
  value: number;
  oppStarterHand?: 'L' | 'R';
}

/** Best price per book at the chosen line (the odds ladder table). */
export interface PropOddsLadderRow {
  book: string;
  over: number | null;
  under: number | null;
  capturedAt: string;
}

/** A priced prop for this player today (a category tab). */
export interface PropCategory {
  propType: string;
  line: number | null;
  l10pct: number;
}

export interface PlayerPayload {
  player: {
    id: number;
    name: string;
    teamAbbrev: string | null;
    position: string | null;
    batSide: string | null;
    throwHand: string | null;
    headshot: string | null;
  };
  propType: string;
  line: number | null;
  seasonToggle: string[];
  categories: PropCategory[];
  series: PropGameStatPoint[];
  rates: PropRates;
  averages: { l5: number; l10: number; season: number };
  streak: number;
  grade: DeskGrade | null;
  oddsLadder: PropOddsLadderRow[];
  /** Provider-shaped season/vs-hand splits keyed `${season}:${scope}:${group}`. */
  splits: Record<string, unknown>;
  lineup: { eventId: number; order: number | null; confirmed: boolean; role: string } | null;
  updatedAt: string;
}

/** Public-safe pass identity from POST /props/pass/verify (never the code). */
export interface PressPassInfo {
  role: 'owner' | 'tester' | 'member';
  label: string;
}

/** Thrown by the props client when the Press Pass gate rejects the request. */
export class PressPassRequiredError extends Error {
  constructor(message = 'Press Pass required') {
    super(message);
    this.name = 'PressPassRequiredError';
  }
}

// ---------------------------------------------------------------------------
// Shop types — mirrors backend/src/routes/api.ts's shop endpoints. Products
// are synced from Printful's dashboard (see backend printfulService.ts), not
// hardcoded — this is a live-changing catalog.
// ---------------------------------------------------------------------------

export interface ShopProduct {
  id: string;
  groupId: string;
  slug: string;
  name: string;
  description: string | null;
  retailPriceCents: number;
  currency: string;
  imageUrl: string | null;
}

export interface CartItemInput {
  id: string;
  quantity: number;
}

export interface ShippingAddressInput {
  name: string;
  address1: string;
  address2?: string;
  city: string;
  state?: string;
  zip: string;
  country: string;
  phone?: string;
  email?: string;
}

export interface CreatePaymentIntentResponse {
  paymentIntentId: string;
  clientSecret: string;
  totalCents: number;
  shippingCents: number;
  currency: string;
  reused: boolean;
}

/** Only the address fields a live shipping quote needs. */
export interface ShippingQuoteAddressInput {
  address1?: string;
  city?: string;
  state?: string;
  zip?: string;
  country: string;
}

export interface ShippingQuote {
  subtotalCents: number;
  shippingCents: number;
  totalCents: number;
  currency: string;
  /** True when the live Printful rate was unavailable and a flat fallback was used. */
  shippingEstimated: boolean;
}

export interface ShopOrderStatus {
  status: string;
  trackingUrl?: string | null;
  environment: 'live' | 'test';
  createdAt: string;
  totalCents: number;
  currency: string;
  items: Array<{ productId: string; name: string; quantity: number; unitPriceCents: number }>;
}

export interface ShopConfig { mode: 'live' | 'sandbox' }

export type PaymentSource =
  | { kind: 'form'; shippingAddress: ShippingAddressInput; email?: string }
  | { kind: 'express'; confirmationTokenId: string };

export class APIRequestError extends Error {
  constructor(readonly status: number, message: string, readonly data: unknown) {
    super(message);
  }
  get code(): string | undefined {
    return typeof this.data === 'object' && this.data && 'code' in this.data
      ? String((this.data as { code: unknown }).code)
      : undefined;
  }
}

export interface BackendTeam {
  id: string;
  sport: 'college-football' | 'nfl';
  name: string;
  displayName: string;
  abbreviation: string;
  mascot?: string;
  location: string;
  conference?: string;
  division?: string;
  primaryColor: string;
  secondaryColor?: string;
  logo: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  dataSource: string;
}

class APIClient {
  private baseURL: string;
  private timeout: number;

  constructor() {
    this.baseURL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api';
    this.timeout = 10000; // 10 seconds
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<APIResponse<T>> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);
    options.signal?.addEventListener('abort', () => controller.abort(), { once: true });

    try {
      const response = await fetch(`${this.baseURL}${endpoint}`, {
        ...options,
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
      });

      clearTimeout(timeoutId);

      // Parse defensively: an error response may carry a non-JSON body (a proxy
      // 502, a 413, plain text), and blindly awaiting .json() there would throw
      // a parse error that masks the real status code.
      let data: any = null;
      try {
        data = await response.json();
      } catch {
        data = null;
      }

      if (!response.ok) {
        const message =
          typeof data?.message === 'string' && data.message
            ? data.message
            : `API Error: ${response.status} ${response.statusText}`;
        throw new APIRequestError(response.status, message, data?.data);
      }

      // Convert timestamp strings to Date objects safely
      if (data?.meta) {
        data.meta.timestamp = createDateWithFallback(data.meta.timestamp, getCurrentDate());
      }

      return data;
    } catch (error) {
      clearTimeout(timeoutId);
      if (error instanceof Error && error.name === 'AbortError') {
        if (options.signal?.aborted) throw error;
        throw new Error('Request timeout');
      }
      throw error;
    }
  }

  /**
   * Get teams by sport
   */
  async getTeams(
    sport?: 'college-football' | 'nfl',
    options: {
      conference?: string;
      division?: string;
      active?: boolean;
      page?: number;
      limit?: number;
    } = {}
  ): Promise<BackendTeam[]> {
    const params = new URLSearchParams();

    if (sport) params.append('sport', sport);
    if (options.conference) params.append('conference', options.conference);
    if (options.division) params.append('division', options.division);
    if (options.active !== undefined) params.append('active', options.active.toString());
    if (options.page) params.append('page', options.page.toString());
    if (options.limit) params.append('limit', options.limit.toString());

    const response = await this.request<BackendTeam[]>(`/teams?${params.toString()}`);
    return response.data;
  }

  /**
   * Get games with filtering
   */
  async getGames(
    options: {
      sport?: 'college-football' | 'nfl';
      season?: number;
      week?: number;
      status?: string;
      team?: string;
      live?: boolean;
      date?: string;
      startDate?: string;
      endDate?: string;
      page?: number;
      limit?: number;
    } = {}
  ): Promise<BackendGame[]> {
    const params = new URLSearchParams();

    Object.entries(options).forEach(([key, value]) => {
      if (value !== undefined) {
        params.append(key, value.toString());
      }
    });

    const response = await this.request<BackendGame[]>(`/games?${params.toString()}`);
    return response.data;
  }

  /**
   * Get scoreboard via the ESPN proxy endpoint.
   * Returns ScoreboardGame[] for any supported league.
   */
  async getScoreboard(options: {
    league: string;
    startDate?: string;
    endDate?: string;
  }): Promise<ScoreboardGame[]> {
    const params = new URLSearchParams();
    params.append('league', options.league);
    if (options.startDate) params.append('startDate', options.startDate);
    if (options.endDate) params.append('endDate', options.endDate);

    const response = await this.request<ScoreboardGame[]>(`/scoreboard?${params.toString()}`);
    return response.data;
  }

  /**
   * Get AI predictions (full matchup breakdowns) for a league over a date range.
   * Defaults handled server-side (today..+3 days).
   */
  async getPredictions(options: {
    league: string;
    startDate?: string;
    endDate?: string;
  }): Promise<GamePrediction[]> {
    const params = new URLSearchParams();
    params.append('league', options.league);
    if (options.startDate) params.append('startDate', options.startDate);
    if (options.endDate) params.append('endDate', options.endDate);

    const response = await this.request<GamePrediction[]>(`/predictions?${params.toString()}`);
    return response.data;
  }

  /**
   * Get the public auto-graded track record ("The Record").
   */
  async getPredictionRecord(): Promise<PredictionRecord> {
    const response = await this.request<PredictionRecord>('/predictions/record');
    return response.data;
  }

  /** The flat graded-leg dataset powering the client-side pivot engine. */
  async getRecordLegs(): Promise<RecordLeg[]> {
    const response = await this.request<RecordLeg[]>('/predictions/record/legs');
    return response.data;
  }

  /**
   * Get live games
   */
  async getLiveGames(): Promise<BackendGame[]> {
    const response = await this.request<BackendGame[]>('/games/live');
    return response.data;
  }

  /**
   * Get current week games for both sports
   */
  async getCurrentWeekGames(): Promise<{ college: BackendGame[]; nfl: BackendGame[] }> {
    const [collegeGames, nflGames] = await Promise.all([
      this.getGames({ sport: 'college-football' }),
      this.getGames({ sport: 'nfl' })
    ]);

    return {
      college: collegeGames,
      nfl: nflGames
    };
  }

  /**
   * Get the live, active merch catalog.
   */
  async getShopProducts(): Promise<ShopProduct[]> {
    const response = await this.request<ShopProduct[]>('/shop/products');
    return response.data;
  }

  async getShopConfig(): Promise<ShopConfig> {
    const response = await this.request<ShopConfig>('/shop/config');
    return response.data;
  }

  /**
   * Price a cart against the live catalog and create a Stripe PaymentIntent.
   * The server recomputes the authoritative total from its own product
   * table — the client never sends a price.
   */
  async createPaymentIntent(
    items: CartItemInput[],
    source: PaymentSource,
    expectedTotalCents: number,
    checkoutAttemptId: string
  ): Promise<CreatePaymentIntentResponse> {
    const response = await this.request<CreatePaymentIntentResponse>('/shop/payment-intent', {
      method: 'POST',
      body: JSON.stringify({ items, expectedTotalCents, checkoutAttemptId, source }),
    });
    return response.data;
  }

  /**
   * Live shipping + total for a cart shipping to a given address. Read-only —
   * no PaymentIntent or order is created — so the checkout UI can show the real
   * total (computed the same way the charge will be) before the customer pays.
   */
  async getShippingQuote(
    items: CartItemInput[],
    shippingAddress: ShippingQuoteAddressInput,
    signal?: AbortSignal
  ): Promise<ShippingQuote> {
    const response = await this.request<ShippingQuote>('/shop/shipping-quote', {
      method: 'POST',
      body: JSON.stringify({ items, shippingAddress }),
      signal,
    });
    return response.data;
  }

  /**
   * PII-free order status for the checkout confirmation screen.
   */
  async getShopOrder(paymentIntentId: string): Promise<ShopOrderStatus> {
    const response = await this.request<ShopOrderStatus>(`/shop/order/${encodeURIComponent(paymentIntentId)}`);
    return response.data;
  }

  /**
   * Props read (The Big Board / meta). Sends the Press Pass as `X-Press-Pass`
   * when we hold one. A 401 means the gate rejected us — surfaced as a typed
   * PressPassRequiredError so the page can show the paste-a-code state instead
   * of a generic error. Other non-2xx bubble as normal Errors.
   */
  private async propsRequest<T>(endpoint: string): Promise<APIResponse<T>> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);
    const pass = getPass();
    try {
      const response = await fetch(`${this.baseURL}${endpoint}`, {
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          ...(pass ? { 'X-Press-Pass': pass } : {}),
        },
      });
      clearTimeout(timeoutId);

      let data: any = null;
      try {
        data = await response.json();
      } catch {
        data = null;
      }

      if (response.status === 401) {
        throw new PressPassRequiredError(
          typeof data?.message === 'string' && data.message ? data.message : 'Press Pass required'
        );
      }
      if (!response.ok) {
        const message =
          typeof data?.message === 'string' && data.message
            ? data.message
            : `API Error: ${response.status} ${response.statusText}`;
        throw new Error(message);
      }
      return data;
    } catch (error) {
      clearTimeout(timeoutId);
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error('Request timeout');
      }
      throw error;
    }
  }

  /** The Big Board for a league + date (defaults to today, server-side). */
  async getPropsBoard(options: { league: string; date?: string }): Promise<BoardPayload> {
    const params = new URLSearchParams();
    params.append('league', options.league);
    if (options.date) params.append('date', options.date);
    const response = await this.propsRequest<BoardPayload>(`/props/board?${params.toString()}`);
    return response.data;
  }

  /** Board metadata: categories, games today, books, lane freshness. */
  async getPropsMeta(league: string): Promise<PropsMeta> {
    const response = await this.propsRequest<PropsMeta>(`/props/meta?league=${encodeURIComponent(league)}`);
    return response.data;
  }

  /**
   * One player's page: game-log series vs the line, all hit-rate windows, odds
   * ladder, splits, lineup. Always requires a valid Press Pass (throws
   * PressPassRequiredError on 401) — player pages are above the free split line.
   */
  async getPropsPlayer(options: {
    league: string;
    playerId: number;
    propType?: string;
    line?: number;
    eventId?: number;
  }): Promise<PlayerPayload> {
    const params = new URLSearchParams();
    params.append('league', options.league);
    if (options.propType) params.append('propType', options.propType);
    if (options.line != null && Number.isFinite(options.line)) params.append('line', String(options.line));
    if (options.eventId != null && Number.isFinite(options.eventId)) params.append('eventId', String(options.eventId));
    const response = await this.propsRequest<PlayerPayload>(
      `/props/player/${encodeURIComponent(options.playerId)}?${params.toString()}`
    );
    return response.data;
  }

  /**
   * Exchange a Press Pass code for its { role, label }. POSTs the code in the
   * body (never the URL — no log/referrer leak). A 401 means the code is invalid
   * (surfaced as PressPassRequiredError); the code itself is never echoed back.
   */
  async verifyPressPass(code: string): Promise<PressPassInfo> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);
    try {
      const response = await fetch(`${this.baseURL}/props/pass/verify`, {
        method: 'POST',
        signal: controller.signal,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: code.trim() }),
      });
      clearTimeout(timeoutId);

      let data: any = null;
      try {
        data = await response.json();
      } catch {
        data = null;
      }

      if (response.status === 401) {
        throw new PressPassRequiredError(
          typeof data?.message === 'string' && data.message ? data.message : 'That Press Pass is not valid.'
        );
      }
      if (!response.ok) {
        throw new Error(
          typeof data?.message === 'string' && data.message ? data.message : `API Error: ${response.status}`
        );
      }
      return data.data as PressPassInfo;
    } catch (error) {
      clearTimeout(timeoutId);
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error('Request timeout');
      }
      throw error;
    }
  }

  /**
   * Get system health
   */
  async getHealth(): Promise<any> {
    const response = await this.request<any>('/health');
    return response.data;
  }

  /**
   * Clear cache (admin function)
   */
  async clearCache(pattern?: string): Promise<{ cleared: boolean; pattern?: string }> {
    const response = await this.request<{ cleared: boolean; pattern?: string }>('/cache/clear', {
      method: 'POST',
      body: JSON.stringify({ pattern })
    });
    return response.data;
  }

  /**
   * Join waitlist
   */
  async joinWaitlist(email: string, source = 'landing'): Promise<{ id: string; email: string; createdAt: string }> {
    const response = await this.request<{ id: string; email: string; createdAt: string }>('/waitlist', {
      method: 'POST',
      body: JSON.stringify({ email, source })
    });
    return response.data;
  }

  /**
   * Get waitlist count
   */
  async getWaitlistCount(): Promise<number> {
    const response = await this.request<{ count: number }>('/waitlist/count');
    return response.data.count;
  }
}

// Export singleton instance
export const apiClient = new APIClient();

/**
 * Transform backend game to unified game format (for compatibility with existing frontend)
 * SAFE: Uses validated date creation to prevent RangeError
 */
export function transformBackendGame(backendGame: BackendGame) {
  // Safely create dates with fallbacks
  const gameDate = createDateWithFallback(backendGame.scheduledAt, getCurrentDate());
  const lastUpdated = createDateWithFallback(backendGame.lastUpdated, getCurrentDate());

  return {
    id: backendGame.id,
    sport: backendGame.sport,
    season: backendGame.season,
    week: backendGame.week,
    seasonType: backendGame.seasonType,
    date: gameDate,
    status: backendGame.status,
    isNeutralSite: backendGame.isNeutralSite,
    venue: backendGame.venue,
    homeTeam: {
      id: backendGame.homeTeam.id,
      name: backendGame.homeTeam.name,
      abbreviation: backendGame.homeTeam.abbreviation,
      score: backendGame.homeTeam.score,
      logo: backendGame.homeTeam.logo
    },
    awayTeam: {
      id: backendGame.awayTeam.id,
      name: backendGame.awayTeam.name,
      abbreviation: backendGame.awayTeam.abbreviation,
      score: backendGame.awayTeam.score,
      logo: backendGame.awayTeam.logo
    },
    broadcast: backendGame.broadcast,
    attendance: backendGame.attendance,
    lastUpdated,
    dataQuality: backendGame.dataQuality
  };
}

/**
 * Transform backend team to unified team format (for compatibility with existing frontend)
 */
export function transformBackendTeam(backendTeam: BackendTeam) {
  return {
    id: backendTeam.id,
    name: backendTeam.name,
    displayName: backendTeam.displayName,
    abbreviation: backendTeam.abbreviation,
    mascot: backendTeam.mascot,
    location: backendTeam.location,
    conference: backendTeam.conference,
    division: backendTeam.division,
    primaryColor: backendTeam.primaryColor,
    secondaryColor: backendTeam.secondaryColor,
    logo: backendTeam.logo,
    sport: backendTeam.sport,
    isActive: backendTeam.isActive
  };
}
